// ═══════════════════════════════════════════════════════════════
// ARCHIVO: src/components/ImplementacionPanel.jsx
// INSTRUCCIÓN: Crear este archivo nuevo en src/components/
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';

const API_URL = '';

function authFetch(url, opts) {
  const token = localStorage.getItem('wc_token');
  return fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts && opts.headers ? opts.headers : {}),
      ...(token ? { Authorization: 'Bearer ' + token } : {})
    }
  });
}

const AREAS = [
  { id: 7, nombre: 'Soporte técnico', color: '#58a6ff', key: 'st' },
  { id: 10, nombre: 'Ventas instalaciones', color: '#3fb950', key: 'vi' },
  { id: 9, nombre: 'Recover', color: '#d29922', key: 'rc' },
];

const STATUS_BADGE = {
  'Abierto': { bg: 'rgba(210,153,34,0.12)', color: '#d29922' },
  'Pendiente': { bg: 'rgba(139,148,158,0.1)', color: '#8b949e' },
  'Cerrado': { bg: 'rgba(63,185,80,0.12)', color: '#3fb950' },
  'Esperando respuesta del cliente': { bg: 'rgba(139,148,158,0.1)', color: '#8b949e' },
};

const CAT_COLORS = {
  'LOS': '#f85149',
  'Sin Servicio': '#f85149',
  'Problema': '#bc8cff',
  'Instalacion': '#3fb950',
  'Instalación': '#3fb950',
  'Mudanza': '#58a6ff',
  'Cambio': '#58a6ff',
  'Reconexión': '#d29922',
  'Recupero': '#d29922',
  'Retiro': '#f85149',
};

function getCatColor(cat) {
  if (!cat) return '#8b949e';
  for (const [key, color] of Object.entries(CAT_COLORS)) {
    if (cat.includes(key)) return color;
  }
  return '#8b949e';
}

function formatFecha(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' }) + 
    ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

function formatFechaCorta(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' }) + 
    ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

function ImplementacionPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [areaActiva, setAreaActiva] = useState(null); // null = todas
  const [ticketSeleccionado, setTicketSeleccionado] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('abiertos'); // abiertos | cerrados | todos

  const cargarDatos = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/dashboard/implementacion/tickets`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('Error cargando implementación:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const forzarSincronizacion = async () => {
    setSincronizando(true);
    try {
      await authFetch(`${API_URL}/api/dashboard/implementacion/sincronizar`, { method: 'POST' });
      await cargarDatos();
    } catch (err) {
      console.error('Error sincronizando:', err);
    } finally {
      setSincronizando(false);
    }
  };

  if (loading) {
    return (
      <div className="impl-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center', color: '#8b949e' }}>
          <div style={{ fontSize: '14px', marginBottom: '8px' }}>Cargando implementación...</div>
          <div style={{ fontSize: '12px' }}>Sincronizando con ISPCube</div>
        </div>
      </div>
    );
  }

  const metricas = data?.metricas;
  const todosTickets = data?.tickets || [];
  
  // Filtrar tickets por área activa
  let ticketsFiltrados = areaActiva 
    ? todosTickets.filter(t => t.area_id === areaActiva)
    : todosTickets;

  // Filtrar por status
  if (filtroStatus === 'abiertos') {
    ticketsFiltrados = ticketsFiltrados.filter(t => t.status_id !== 3);
  } else if (filtroStatus === 'cerrados') {
    ticketsFiltrados = ticketsFiltrados.filter(t => t.status_id === 3);
  }

  // Ordenar: más recientes primero
  ticketsFiltrados.sort((a, b) => new Date(b.creado) - new Date(a.creado));

  const areaActivaInfo = AREAS.find(a => a.id === areaActiva);
  const tituloListado = areaActivaInfo ? `Tickets ${areaActivaInfo.nombre.toLowerCase()}` : 'Todos los tickets';

  return (
    <div className="impl-panel">
      {/* HEADER */}
      <div className="impl-header">
        <div className="impl-header-left">
          <div className="impl-header-icon">🔧</div>
          <div>
            <div className="impl-header-title">Implementación</div>
            <div className="impl-header-sub">
              Tickets ISPCube — Última sync: {data?.sincronizado_at ? formatFechaCorta(data.sincronizado_at) : 'Nunca'}
            </div>
          </div>
        </div>
        <button 
          className={`impl-sync-btn ${sincronizando ? 'syncing' : ''}`}
          onClick={forzarSincronizacion}
          disabled={sincronizando}
        >
          {sincronizando ? '⟳ Sincronizando...' : '⟳ Sincronizar'}
        </button>
      </div>

      {/* AREA CARDS */}
      <div className="impl-areas">
        {AREAS.map(area => {
          const m = metricas?.por_area?.[area.id] || {};
          const isActive = areaActiva === area.id;
          return (
            <div 
              key={area.id}
              className={`impl-area-card ${isActive ? 'active' : ''}`}
              style={{ borderTop: `3px solid ${area.color}` }}
              onClick={() => setAreaActiva(isActive ? null : area.id)}
            >
              <div className="impl-area-name">
                <span className="impl-area-dot" style={{ background: area.color }}></span>
                {area.nombre}
              </div>
              <div className="impl-area-metrics">
                <div className="impl-metric">
                  <div className="impl-metric-val" style={{ color: '#d29922' }}>{m.abiertos || 0}</div>
                  <div className="impl-metric-label">Abiertos</div>
                  <div className="impl-metric-today">
                    <span style={{ color: '#3fb950' }}>+{m.abiertos_hoy || 0}</span> hoy
                  </div>
                </div>
                <div className="impl-metric">
                  <div className="impl-metric-val" style={{ color: '#3fb950' }}>{m.cerrados || 0}</div>
                  <div className="impl-metric-label">Cerrados</div>
                  <div className="impl-metric-today">
                    <span style={{ color: '#3fb950' }}>+{m.cerrados_hoy || 0}</span> hoy
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* SUMMARY ROW */}
      {metricas && (
        <div className="impl-summary">
          <div className="impl-summary-card">
            <div className="impl-summary-val">{metricas.total_abiertos}</div>
            <div className="impl-summary-label">Total abiertos</div>
          </div>
          <div className="impl-summary-card">
            <div className="impl-summary-val" style={{ color: '#3fb950' }}>{metricas.cerrados_hoy}</div>
            <div className="impl-summary-label">Cerrados hoy</div>
          </div>
          <div className="impl-summary-card">
            <div className="impl-summary-val" style={{ color: '#d29922' }}>{metricas.nuevos_hoy}</div>
            <div className="impl-summary-label">Nuevos hoy</div>
          </div>
          <div className="impl-summary-card">
            <div className="impl-summary-val" style={{ color: '#58a6ff' }}>{metricas.tiempo_promedio_dias}d</div>
            <div className="impl-summary-label">Tiempo promedio</div>
          </div>
        </div>
      )}

      {/* CONTENT: LISTADO + DETALLE */}
      <div className="impl-content">
        {/* LISTADO DE TICKETS */}
        <div className="impl-tickets-section">
          <div className="impl-tickets-header">
            <div className="impl-tickets-title">{tituloListado}</div>
            <div className="impl-tickets-filters">
              {['abiertos', 'cerrados', 'todos'].map(f => (
                <button 
                  key={f}
                  className={`impl-filter-btn ${filtroStatus === f ? 'active' : ''}`}
                  onClick={() => setFiltroStatus(f)}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
              <span className="impl-tickets-count">{ticketsFiltrados.length}</span>
            </div>
          </div>

          {/* Header row */}
          <div className="impl-ticket-row impl-ticket-head">
            <div>#</div>
            <div>Cliente</div>
            <div>Categoría</div>
            <div>Asignado</div>
            <div>Estado</div>
          </div>

          {/* Ticket rows */}
          <div className="impl-tickets-list">
            {ticketsFiltrados.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#484f58', fontSize: '13px' }}>
                No hay tickets {filtroStatus !== 'todos' ? filtroStatus : ''} en esta área
              </div>
            ) : (
              ticketsFiltrados.slice(0, 50).map(ticket => {
                const isSelected = ticketSeleccionado?.id === ticket.id;
                const catColor = getCatColor(ticket.categoria);
                const statusStyle = STATUS_BADGE[ticket.status] || STATUS_BADGE['Abierto'];
                
                return (
                  <div 
                    key={ticket.id}
                    className={`impl-ticket-row ${isSelected ? 'selected' : ''}`}
                    onClick={() => setTicketSeleccionado(ticket)}
                  >
                    <div className="impl-t-id">#{ticket.id}</div>
                    <div className="impl-t-client">
                      <div className="impl-t-name">{ticket.cliente_nombre || 'Sin nombre'}</div>
                      <div className="impl-t-addr">
                        {[ticket.cliente_direccion, ticket.cliente_ciudad].filter(Boolean).join(', ') || 'Sin dirección'}
                      </div>
                    </div>
                    <div>
                      <span className="impl-badge" style={{ background: `${catColor}18`, color: catColor }}>
                        {ticket.categoria || 'Sin cat.'}
                      </span>
                    </div>
                    <div className="impl-t-asig">{ticket.asignado || 'Sin asignar'}</div>
                    <div>
                      <span className="impl-badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                        {ticket.status}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* DETALLE DEL TICKET */}
        <div className="impl-detail-section">
          {ticketSeleccionado ? (
            <>
              <div className="impl-detail-header">
                🎫 Ticket #{ticketSeleccionado.id}
              </div>
              <div className="impl-detail-body">
                <div className="impl-detail-row">
                  <span className="impl-dl">Cliente</span>
                  <span className="impl-dv">{ticketSeleccionado.cliente_nombre}</span>
                </div>
                <div className="impl-detail-row">
                  <span className="impl-dl">Código</span>
                  <span className="impl-dv">{ticketSeleccionado.cliente_codigo}</span>
                </div>
                <div className="impl-detail-row">
                  <span className="impl-dl">Dirección</span>
                  <span className="impl-dv">{ticketSeleccionado.cliente_direccion || '—'}</span>
                </div>
                {ticketSeleccionado.entre_calles && (
                  <div className="impl-detail-row">
                    <span className="impl-dl">Entre calles</span>
                    <span className="impl-dv">{ticketSeleccionado.entre_calles}</span>
                  </div>
                )}
                <div className="impl-detail-row">
                  <span className="impl-dl">Ciudad</span>
                  <span className="impl-dv">{ticketSeleccionado.cliente_ciudad || '—'}</span>
                </div>
                <div className="impl-detail-row">
                  <span className="impl-dl">Teléfono</span>
                  <span className="impl-dv" style={{ color: '#58a6ff' }}>{ticketSeleccionado.cliente_telefono || '—'}</span>
                </div>
                <div className="impl-detail-row">
                  <span className="impl-dl">Plan</span>
                  <span className="impl-dv">{ticketSeleccionado.cliente_plan || '—'}</span>
                </div>
                <div className="impl-detail-row">
                  <span className="impl-dl">Nodo</span>
                  <span className="impl-dv">{ticketSeleccionado.cliente_nodo || '—'}</span>
                </div>
                <div className="impl-detail-row">
                  <span className="impl-dl">Deuda</span>
                  <span className="impl-dv" style={{ color: parseFloat(ticketSeleccionado.cliente_deuda) > 0 ? '#f85149' : '#3fb950' }}>
                    ${ticketSeleccionado.cliente_deuda}
                  </span>
                </div>
                <div className="impl-detail-row">
                  <span className="impl-dl">Área</span>
                  <span className="impl-dv" style={{ color: AREAS.find(a => a.id === ticketSeleccionado.area_id)?.color || '#8b949e' }}>
                    {ticketSeleccionado.area}
                  </span>
                </div>
                <div className="impl-detail-row">
                  <span className="impl-dl">Categoría</span>
                  <span className="impl-dv">
                    <span className="impl-badge" style={{ background: `${getCatColor(ticketSeleccionado.categoria)}18`, color: getCatColor(ticketSeleccionado.categoria) }}>
                      {ticketSeleccionado.categoria}
                    </span>
                  </span>
                </div>
                <div className="impl-detail-row">
                  <span className="impl-dl">Asignado</span>
                  <span className="impl-dv">{ticketSeleccionado.asignado || 'Sin asignar'}</span>
                </div>
                <div className="impl-detail-row">
                  <span className="impl-dl">Operador</span>
                  <span className="impl-dv">{ticketSeleccionado.operador || '—'}</span>
                </div>
                <div className="impl-detail-row">
                  <span className="impl-dl">Creado</span>
                  <span className="impl-dv">{formatFecha(ticketSeleccionado.creado)}</span>
                </div>
                {ticketSeleccionado.cerrado && (
                  <div className="impl-detail-row">
                    <span className="impl-dl">Cerrado</span>
                    <span className="impl-dv">{formatFecha(ticketSeleccionado.cerrado)}</span>
                  </div>
                )}
                <div className="impl-detail-row">
                  <span className="impl-dl">Estado</span>
                  <span className="impl-dv">
                    <span className="impl-badge" style={{ ...(STATUS_BADGE[ticketSeleccionado.status] || {}) }}>
                      {ticketSeleccionado.status}
                    </span>
                  </span>
                </div>

                {/* Historial */}
                {ticketSeleccionado.items && ticketSeleccionado.items.length > 0 && (
                  <div className="impl-detail-items">
                    <div className="impl-detail-items-title">HISTORIAL</div>
                    {ticketSeleccionado.items.map((item, i) => (
                      <div key={i} className="impl-detail-item">
                        <div className="impl-detail-item-user">
                          {item.usuario} — {formatFechaCorta(item.fecha)}
                          {item.interno && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#484f58' }}>(interno)</span>}
                        </div>
                        {item.contenido}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#484f58' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🎫</div>
              <div style={{ fontSize: '14px' }}>Seleccioná un ticket para ver el detalle</div>
            </div>
          )}
        </div>
      </div>

      {/* GRÁFICO 7 DÍAS */}
      {metricas?.ultimos_7_dias && (
        <div className="impl-chart-section">
          <div className="impl-chart-title">Tickets últimos 7 días</div>
          <div className="impl-chart-bars">
            {metricas.ultimos_7_dias.map((dia, i) => {
              const maxVal = Math.max(...metricas.ultimos_7_dias.map(d => Math.max(d.abiertos, d.cerrados)), 1);
              return (
                <div key={i} className="impl-chart-bar-group">
                  <div className="impl-chart-bar-stack">
                    <div className="impl-chart-bar opened" style={{ height: `${(dia.abiertos / maxVal) * 80}px` }}></div>
                    <div className="impl-chart-bar closed" style={{ height: `${(dia.cerrados / maxVal) * 80}px` }}></div>
                  </div>
                  <div className="impl-chart-bar-nums">
                    {dia.abiertos > 0 && <span style={{ color: '#58a6ff' }}>{dia.abiertos}</span>}
                    {dia.cerrados > 0 && <span style={{ color: '#3fb950' }}>{dia.cerrados}</span>}
                  </div>
                  <div className="impl-chart-label">{i === 6 ? 'Hoy' : dia.dia}</div>
                </div>
              );
            })}
          </div>
          <div className="impl-chart-legend">
            <span><span className="impl-legend-dot" style={{ background: '#58a6ff' }}></span>Abiertos</span>
            <span><span className="impl-legend-dot" style={{ background: '#3fb950' }}></span>Cerrados</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImplementacionPanel;
